import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Text } from '@lili/lili-frontend-common';
import DigitsContainer from '../../../generic-components/DigitsContainer';
import LoginCard from '../components/LoginCard';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';

import { VERIFICATION_CODE_PAGE_DATA } from '../constants';
import { ASTERISK, DOT } from '../../../constants/general';
import { LOGIN_NAVIGATION_MAP } from '../components/LoginNavigation/constants';
import { useStore } from '../../../store';
import { useVerifyMutation } from '../../../hooks/mutations/useVerifyMutation';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { LOGIN_ERRORS, LOGIN_STATUS } from '../constants/messages';
import { SERVER_ERRORS_DATA } from '../../../constants/serverErrors';
import { setToSessionStorage } from '../../../services/storage.service';
import { initBraze } from '../../../services/braze.service';
import RememberMfaLogin from '../components/RememberMfaLogin';
import { IClientResponse } from '../../profile/components/accountant-account-switcher/types';
import { Layout } from '../../../components/Layout/Layout';
import { useAdaService } from '../../../hooks/services/useAdaService';

const VerificationCode = () => {
  const {
    LOGIN_CARD,
    TEXT_ROW_1,
    TEXT_ROW_2,
    TEXT_ROW_3,
    NUM_OF_DIGITS_SMS,
    NUM_OF_DIGITS_VOICE,
    HEADER_VERIFICATION_CODE,
    ERROR_MESSAGE_CLASS_NAME,
    MFA_VOICE,
    MFA_SMS,
  } = VERIFICATION_CODE_PAGE_DATA;

  const navigate = useNavigate();

  const setCurrentPage = useStore((state) => state.setCurrentPage);

  const user = useStore((state) => state.user);

  const resetUserSliceId = useStore((state) => state.resetUserSliceId);

  const searchParams = useStore((state) => state.searchParams);

  const getSummaryData = useStore((state) => state.getSummaryData);

  const setShowAccountantSwitcherModal = useStore((state) => state.setShowAccountantSwitcherModal);

  const [shouldRemember, setShouldRemember] = useState<boolean>(false);

  const { startAdaChat } = useAdaService();

  const { refetch: mutateSummary } = useAccountSummaryQuery(async () => {
    const { authorizedUserData } = getSummaryData();
    const combinedLists: IClientResponse[] = [
      ...(authorizedUserData?.authorizedUserInvites || []),
      ...(authorizedUserData?.authorizedUserPendingInvites || []),
    ];
    if (combinedLists.length > 1) {
      setTimeout(() => {
        setShowAccountantSwitcherModal(true);
      }, 1000);
    }
    await startAdaChat();
    navigate('/dashboard');
    setCurrentPage(0);
  });

  const handleUserAutoSwitch = () => {
    if (!searchParams) return;
    const qParamsObject = new URLSearchParams(searchParams);
    const crid = qParamsObject.get('crid');
    if (crid) {
      /** Update LocalStorage with new customerId */
      setToSessionStorage({ key: 'customerId', value: crid });
      /** Reset logged-in user id */
      resetUserSliceId(crid);
    }
  };

  const { mutate, isError, isLoading } = useVerifyMutation(false, async (status) => {
    if (status === LOGIN_STATUS.PASSWORD_EXPIRED) {
      setCurrentPage(LOGIN_NAVIGATION_MAP.PASSWORD_EXPIRED);
    } else {
      setToSessionStorage({ key: 'customerId', value: JSON.stringify(user?.customerId || '') });
      handleUserAutoSwitch();
      await mutateSummary();
      await initBraze({ customerId: user.customerId });
    }
  });

  const handleFulfill = async (otp: string) => {
    mutate(
      {
        customerId: user?.customerId,
        answer: otp,
        customData: { ...user.loginResponseCustomData },
        mfaType: user.mfaType ?? MFA_SMS,
        rememberMe: shouldRemember,
        authUserType: user?.authorizedUserType || '',
        authUserId: user?.authorizedUserId || '',
      },
      {
        onError: (errorObj: any) => {
          if (
            errorObj?.response?.data?.errors?.[0]?.code === SERVER_ERRORS_DATA.USER_LOCKED_CODE ||
            errorObj?.response?.data?.errors?.[0]?.codeName === SERVER_ERRORS_DATA.USER_LOCKED_CODE_NAME
          ) {
            setCurrentPage(LOGIN_NAVIGATION_MAP.TAKE_5);
          }
        },
      },
    );
  };

  const getNumberOfDigits = () => {
    if (user.mfaType === MFA_VOICE) return NUM_OF_DIGITS_VOICE;
    return NUM_OF_DIGITS_SMS;
  };

  const handleOnResendClick = () => {
    setCurrentPage(LOGIN_NAVIGATION_MAP.RESEND_VERIFICATION);
  };

  const { t } = useTranslation();

  return (
    <Layout shouldHideProfile shouldHideChatBot wrapperClassName='pt-[78px] pl-[72px] ml-[100px] pc-md:ml-[256px]'>
      <LoginCard className={LOGIN_CARD.CLASS_NAME}>
        <Heading title={t(HEADER_VERIFICATION_CODE.TITLE)} className={HEADER_VERIFICATION_CODE.CLASS_NAME} />
        <Heading title={t(TEXT_ROW_1.TITLE)} className={TEXT_ROW_1.CLASS_NAME} />
        <Heading
          title={t(TEXT_ROW_2.TITLE) + (user.mfaDestination ?? '').replaceAll(ASTERISK, DOT)}
          className={TEXT_ROW_2.CLASS_NAME}
        />
        <DigitsContainer
          numOfDigits={getNumberOfDigits()}
          onFulfill={handleFulfill}
          isLoading={isLoading}
          isError={isError}
        />
        <div className={ERROR_MESSAGE_CLASS_NAME}>{isError ? t(LOGIN_ERRORS.BAD_REQUEST) : ''}</div>
        <RememberMfaLogin
          checkboxValue={shouldRemember}
          handleCheck={(isChecked: boolean) => setShouldRemember(isChecked)}
        />
        <div className={TEXT_ROW_3.CONTAINER.CLASS_NAME}>
          <Heading title={t(TEXT_ROW_3.TITLE)} className={TEXT_ROW_3.CLASS_NAME} />
          <Button
            id='resendCode'
            onClick={handleOnResendClick}
            title={t(TEXT_ROW_3.INTERNAL_BUTTON.TITLE)}
            className={TEXT_ROW_3.INTERNAL_BUTTON.CLASS_NAME}
          />
        </div>
        <div className='flex items-center justify-center text-center mt-[28px]'>
          <Text.H11 className='text-center  text-text-main-tertiary'>{t('VERIFICATION_CODE_PAGE_FOOTER')}</Text.H11>
        </div>
      </LoginCard>
    </Layout>
  );
};

export default VerificationCode;
